// src/components/PassStatus.js
import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { CreditCard, Calendar, Clock, ReceiptText, ChevronLeft, ChevronRight } from 'lucide-react';

const PassStatus = ({ subscription, payments }) => {
  const navigate = useNavigate();

  // ONLY ADDITION: Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  // ONLY ADDITION: Calculate paginated payments
  const paginatedData = useMemo(() => {
    if (!payments || payments.length === 0) return { paginatedPayments: [], totalPages: 0 };
    
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedPayments = payments.slice(startIndex, endIndex);
    const totalPages = Math.ceil(payments.length / itemsPerPage);
    
    return { paginatedPayments, totalPages };
  }, [payments, currentPage, itemsPerPage]);

  // ONLY ADDITION: Handle page change
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // ONLY ADDITION: Reset to first page when payments change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [payments]);

  // UNCHANGED: Original functions
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(amount);
  };

  // UNCHANGED: Original AccessPassStatus component
  const AccessPassStatus = () => {
    if (!subscription) return null;

    const getStatusText = () => {
      switch (subscription.status) {
        case 'active':
          return (
            <div className="flex flex-col">
              <div className="flex items-center mb-2">
                <span className="bg-green-100 text-green-800 px-2 py-1 rounded text-sm mr-2">Active</span>
                <p className="text-gray-600">
                  Valid until: {formatDate(subscription.expiresAt)}
                </p>
              </div>
              {subscription.timeRemaining && (
                <div className="flex items-center text-gray-600 mb-2">
                  <Clock className="w-4 h-4 mr-2" />
                  <span>{subscription.timeRemaining.days} days remaining</span>
                </div>
              )}
              <p className="text-sm text-gray-500 mt-2">
                Your access pass will expire automatically with no renewal charges. You can purchase a new pass after this one expires.
              </p>
            </div>
          );
          
        case 'trial':
          return (
            <div>
              <div className="flex items-center mb-2">
                <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-sm mr-2">Trial</span>
                <p className="text-gray-600">
                  {subscription.trialGenerationsLeft} free generations remaining
                </p>
              </div>
              <p className="text-sm text-gray-500 mt-2">
                You have {subscription.trialGenerationsLeft} free generations to try out our service. Each view or revision counts as one generation. Purchase a full access pass for unlimited use during the pass period.
              </p>
              <button
                onClick={() => navigate('/payment')}
                className="mt-4 bg-black text-white px-4 py-2 rounded hover:bg-gray-800 flex items-center"
              >
                <CreditCard className="mr-2" size={18} />
                Purchase Access Pass
              </button>
            </div>
          );

        case 'expired':
          return (
            <div>
              <div className="flex items-center mb-2">
                <span className="bg-red-100 text-red-800 px-2 py-1 rounded text-sm mr-2">Expired</span>
                <p className="text-gray-600">
                  Your access pass expired on {formatDate(subscription.expiresAt)}
                </p>
              </div>
              <button
                onClick={() => navigate('/payment')}
                className="mt-4 bg-black text-white px-4 py-2 rounded hover:bg-gray-800 flex items-center"
              >
                <CreditCard className="mr-2" size={18} />
                Purchase Access Pass
              </button>
            </div>
          );
        default:
          return (
            <div>
              <div className="flex items-center mb-2">
                <span className="bg-gray-100 text-gray-800 px-2 py-1 rounded text-sm mr-2">Inactive</span>
                <p className="text-gray-600">
                  You don't have an active access pass.
                </p>
              </div>
              <p className="text-sm text-gray-500 mb-4">
                Each view or revision counts as one generation. Purchase a full access pass for unlimited use during the pass period.
              </p>
              <button
                onClick={() => navigate('/payment')}
                className="mt-4 bg-black text-white px-4 py-2 rounded hover:bg-gray-800 flex items-center"
              >
                <CreditCard className="mr-2" size={18} />
                Purchase Access Pass
              </button>
            </div>
          );
      }
    };

    return getStatusText();
  };

  // ONLY ADDITION: Pagination component
  const Pagination = () => {
    const { totalPages } = paginatedData;
    
    if (totalPages <= 1) return null;

    const getPageNumbers = () => {
      const pages = [];
      const maxVisiblePages = 5;
      
      if (totalPages <= maxVisiblePages) {
        for (let i = 1; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        if (currentPage <= 3) {
          pages.push(1, 2, 3, 4, '...', totalPages);
        } else if (currentPage >= totalPages - 2) {
          pages.push(1, '...', totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
        } else {
          pages.push(1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages);
        }
      }
      
      return pages;
    };

    return (
      <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200">
        <div className="text-sm text-gray-700">
          Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, payments.length)} of {payments.length} payments
        </div>
        
        <div className="flex items-center space-x-2">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className={`p-2 rounded-md ${
              currentPage === 1
                ? 'text-gray-400 cursor-not-allowed'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <ChevronLeft size={18} />
          </button>

          {getPageNumbers().map((page, index) => (
            <button
              key={index}
              onClick={() => typeof page === 'number' && handlePageChange(page)}
              disabled={typeof page !== 'number'}
              className={`px-3 py-1 rounded-md text-sm ${
                page === currentPage
                  ? 'bg-black text-white'
                  : typeof page === 'number'
                  ? 'text-gray-600 hover:bg-gray-100'
                  : 'text-gray-400 cursor-default'
              }`}
            >
              {page}
            </button>
          ))}

          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className={`p-2 rounded-md ${
              currentPage === totalPages
                ? 'text-gray-400 cursor-not-allowed'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <ChevronRight size={18} />
          </button>
        </div>
      </div>
    );
  };

  return (
    <>
      {/* UNCHANGED: Access Pass Information */}
      <div className="bg-white shadow rounded-lg p-6 mb-6">
        <h2 className="text-xl font-semibold mb-4 flex items-center">
          <Calendar className="mr-2" size={20} />
          Access Pass Status
        </h2>

        <AccessPassStatus />
      </div>

      {/* MINIMAL CHANGE: Payment History - only changed payments.map to paginatedData.paginatedPayments.map and added Pagination */}
      {payments && payments.length > 0 && (
        <div className="bg-white shadow rounded-lg p-6 mb-6">
          <h2 className="text-xl font-semibold mb-4 flex items-center">
            <ReceiptText className="mr-2" size={20} />
            Payment History
          </h2>

          <div className="space-y-3">
            {paginatedData.paginatedPayments.map((payment, index) => (
              <div
                key={payment.id || index}
                className="p-3 border border-gray-200 rounded-md"
              >
                <div className="flex justify-between mb-1">
                  <span className="font-medium">{payment.title || 'Access Pass'}</span>
                  <span>{formatCurrency(payment.amount)}</span>
                </div>
                <div className="text-sm text-gray-600 flex justify-between">
                  <span>Purchased: {formatDate(payment.createdAt)}</span>
                  <span>Valid until: {formatDate(payment.expiresAt)}</span>
                </div>
              </div>
            ))}
          </div>

          {/* ONLY ADDITION: Pagination Controls */}
          <Pagination />
        </div>
      )}

      {/* UNCHANGED: Payment Options */}
      <div className="bg-white shadow rounded-lg p-6">
        <h2 className="text-xl font-semibold mb-4 flex items-center">
          <CreditCard className="mr-2" size={20} />
          Payment Options
        </h2>

        <button
          onClick={() => navigate('/payment')}
          className="w-full bg-black text-white px-4 py-2 rounded hover:bg-gray-800"
        >
          View Access Pass Options
        </button>
      </div>
    </>
  );
};

export default PassStatus;